import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import '../../styles/AdminGlobal.css';
import './Admin.css';

const ContentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'navbar' | 'footer' | 'mega-navbar'>('navbar');
  const [navbarItems, setNavbarItems] = useState<any[]>([]);
  const [footerSections, setFooterSections] = useState<any[]>([]);
  const [megaNavbarItems, setMegaNavbarItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Column definitions for Navbar Items
  const navbarColumnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { field: 'title_en', headerName: 'Title (EN)', sortable: true, filter: true, flex: 1 },
    { field: 'title_bn', headerName: 'Title (BN)', sortable: true, filter: true, flex: 1 },
    { field: 'link', headerName: 'Link', sortable: true, width: 200 },
    { field: 'display_order', headerName: 'Order', sortable: true, width: 100 },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      cellRenderer: (params: any) => params.value ? '✓' : '✗'
    },
    {
      headerName: 'Actions',
      width: 180,
      cellRenderer: (params: any) => (
        <div className="action-buttons">
          <button className="edit-btn">Edit</button>
          <button className="delete-btn">Delete</button>
        </div>
      )
    }
  ];

  // Column definitions for Footer Sections
  const footerColumnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { field: 'title_en', headerName: 'Title (EN)', sortable: true, filter: true, width: 200 },
    { field: 'title_bn', headerName: 'Title (BN)', sortable: true, filter: true, width: 200 },
    { field: 'content_en', headerName: 'Content (EN)', sortable: true, flex: 1 },
    { field: 'display_order', headerName: 'Order', sortable: true, width: 100 },
    {
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              title="Edit"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              title="Delete"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  // Column definitions for Mega Navbar Items
  const megaNavbarColumnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { field: 'category_name', headerName: 'Category', sortable: true, filter: true, flex: 1 },
    { field: 'display_order', headerName: 'Order', sortable: true, width: 100 },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      cellRenderer: (params: any) => params.value ? '✓' : '✗'
    },
    {
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              title="Edit"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              title="Delete"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  const loadContent = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getContent();
      const data = response.data || response;

      // Parse the response based on active tab
      if (activeTab === 'navbar') {
        setNavbarItems(Array.isArray(data.navbar_items) ? data.navbar_items : []);
      } else if (activeTab === 'footer') {
        setFooterSections(Array.isArray(data.footer_sections) ? data.footer_sections : []);
      } else {
        setMegaNavbarItems(Array.isArray(data.mega_navbar_items) ? data.mega_navbar_items : []);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Get current data and column defs based on active tab
  const getCurrentData = () => {
    if (activeTab === 'navbar') return navbarItems;
    if (activeTab === 'footer') return footerSections;
    return megaNavbarItems;
  };

  const getCurrentColumnDefs = () => {
    if (activeTab === 'navbar') return navbarColumnDefs;
    if (activeTab === 'footer') return footerColumnDefs;
    return megaNavbarColumnDefs;
  };

  // Filter data based on search term
  const currentDataArray = Array.isArray(getCurrentData()) ? getCurrentData() : [];
  const filteredData = currentDataArray.filter((item: any) =>
    item.title_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.title_bn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Content Management</h3>
          <p>Manage navigation menus and footer content</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success">+ Add Item</button>
          <button className="btn btn-secondary" onClick={loadContent}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="content-tabs">
        <button
          className={`tab-btn ${activeTab === 'navbar' ? 'active' : ''}`}
          onClick={() => setActiveTab('navbar')}
        >
          Navbar Items
        </button>
        <button
          className={`tab-btn ${activeTab === 'footer' ? 'active' : ''}`}
          onClick={() => setActiveTab('footer')}
        >
          Footer Sections
        </button>
        <button
          className={`tab-btn ${activeTab === 'mega-navbar' ? 'active' : ''}`}
          onClick={() => setActiveTab('mega-navbar')}
        >
          Mega Navbar
        </button>
      </div>

      <div className="ag-grid-wrapper" style={{ marginTop: '1rem' }}>
        <div className="search-box">
          <input
            type="text"
            placeholder={`Search ${activeTab} items...`}
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredData.length}</span> items found
        </div>

        <div className="ag-grid-container">
          <AgGridReact

            theme={themeQuartz}
            rowData={filteredData || []}
            columnDefs={getCurrentColumnDefs()}
            pagination={true}
            paginationPageSize={20}
            loading={loading}

            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ContentManagement;
